/**
 * Upload scan state. The scanner function (standalone-scan-uploads) records one fileScans document per uploaded file,
 * keyed by sha1("<bucket>/<path>"). A file can be used (downloaded, shared, imported) only when it is clean.
 */
export type ScanStatus = 'pending' | 'clean' | 'infected' | 'unscannable';
export type ScanState = ScanStatus | 'legacy';

export interface ScanRecord {
  status?: string;
  threat?: string;
}

export interface ScanBadge {
  text: string;
  cls: 'scan-pending' | 'scan-infected' | 'scan-unscannable';
}

/**
 * @param record the fileScans document, or null when there is none
 * @param objectCreated when the file was uploaded (storage metadata), or null when unknown
 * @param enforcedFrom files uploaded from this date on must have a clean record; null while enforcement is off
 */
export function resolveScanState(
  record: ScanRecord | null | undefined,
  objectCreated: Date | null,
  enforcedFrom: Date | null,
): ScanState {
  if (record && record.status) {
    switch (record.status) {
      case 'clean':
        return 'clean';
      case 'infected':
        return 'infected';
      case 'unscannable':
        return 'unscannable';
      default:
        return 'pending';
    }
  }
  // No record. Enforcement off, or the age of the file is unknown: do not block a file because of that.
  if (!enforcedFrom || !objectCreated) {
    return 'legacy';
  }
  return objectCreated.getTime() < enforcedFrom.getTime() ? 'legacy' : 'pending';
}

/** Only files under these folders are scanned by the scanner function; everything else has no scan record and is not held back. */
export const DEFAULT_SCANNED_PREFIXES = ['patient/'];

export function isScannedPath(path: string, prefixes: string[] = DEFAULT_SCANNED_PREFIXES): boolean {
  return !!path && prefixes.some(prefix => path.startsWith(prefix));
}

export function isUsable(state: ScanState): boolean {
  return state === 'clean' || state === 'legacy';
}

export function scanMessage(state: ScanState): string {
  switch (state) {
    case 'pending':
      return 'This file is waiting for its security check. It can be opened, shared or imported when the check is finished.';
    case 'infected':
      return 'A threat was found in this file, so it was quarantined and cannot be used.';
    case 'unscannable':
      return 'This file is too large for the security check, so it cannot be used.';
    default:
      return '';
  }
}

export function scanBadge(state: ScanState): ScanBadge | null {
  switch (state) {
    case 'pending':
      return { text: 'Security check pending', cls: 'scan-pending' };
    case 'infected':
      return { text: 'Quarantined: threat found', cls: 'scan-infected' };
    case 'unscannable':
      return { text: 'Too large to scan', cls: 'scan-unscannable' };
    default:
      return null;
  }
}

/** sha1 hex of "<bucket>/<path>", the id of the fileScans document (same as the scanner function). */
export async function scanDocId(bucket: string, path: string): Promise<string> {
  const bytes = new TextEncoder().encode(`${bucket}/${path}`);
  const digest = await crypto.subtle.digest('SHA-1', bytes);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}
